import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { SessionType } from '@leadgab/shared';
import { AlertTriangleIcon } from './icons.tsx';

export function Avatar({
  src,
  name,
  size = 40,
  className,
}: {
  src?: string | null;
  name: string;
  size?: number;
  className?: string;
}): React.JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className={clsx('shrink-0 rounded-full bg-slate-200 object-cover dark:bg-slate-800', className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-200',
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials || '?'}
    </span>
  );
}

const CHANNEL_STYLES: Record<SessionType, { label: string; className: string }> = {
  chat: { label: 'Chat', className: 'bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-200' },
  sms: { label: 'SMS', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200' },
  email: { label: 'Email', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200' },
  fmp: { label: 'Messenger', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200' },
  craigslist: { label: 'Craigslist', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-200' },
  form: { label: 'Form', className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

export function ChannelBadge({ type }: { type: SessionType }): React.JSX.Element {
  const style = CHANNEL_STYLES[type] ?? CHANNEL_STYLES.chat;

  return <span className={clsx('lg-badge', style.className)}>{style.label}</span>;
}

export function Spinner({ className }: { className?: string }): React.JSX.Element {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={clsx(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        className ?? 'h-4 w-4',
      )}
    />
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  fill = false,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  /**
   * Centre in the whole pane rather than sit at the top of it.
   *
   * Opt-in, because most of these live inside a card whose height is its
   * content: `h-full` would have nothing to resolve against there, and the
   * fixed padding is what gives the card its shape. The panes that own their
   * height — the conversation list, the reading pane, the message thread —
   * pass this so the message lands where the eye already is.
   */
  fill?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center px-6 text-center',
        fill ? 'h-full py-10' : 'py-16',
      )}
    >
      {icon ? <div className="mb-4 text-slate-300 dark:text-slate-600">{icon}</div> : null}
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{title}</p>
      {description ? (
        <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function ErrorNotice({ message }: { message: string }): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/60 dark:text-rose-200"
    >
      <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <label className="lg-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

/** Compact, locale-aware relative time: "4m", "3h", "12 Mar". */
export function formatRelative(iso: string): string {
  const date = new Date(iso);
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d`;

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function formatDateHeading(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();

  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}
